import LinearProgress from '@mui/material/LinearProgress';
import _ from 'lodash';
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { AddButton } from '../../styledComponents/GlobalStyle';
import Modal from '../Organisations/Modal';
import InviteUsersForm from './InviteUsersForm';
import UsersListRow from './UsersListRow';
import { Card } from '@mui/material';
import Select from 'react-select';

const UsersListTable = ({ branding, sortedUserList, adminUser, showLoader, canManage, facilityList }) => {
  const [showInviteUserForm, setShowInviteUserForm] = useState(false);
  const [filter, setFilter] = useState({ permission: null, value: null });

  const { body } = branding.fonts?.[0].font;

  const customStyles = {
    groupHeading: (provided) => ({
      ...provided,
      fontSize: '14px',
      fontWeight: 'bold',
      borderTop: '2px solid #ddd',
      borderBottom: '2px solid #ddd',
      color: '#000',
      paddingTop: '5px',
      paddingBottom: '5px',
    }),
  };

  const facilityOptions = facilityList?.map((facility) => {
    return { value: facility?.id, label: facility?.name };
  });

  const permissionOptions = [
    { value: 'organisation', label: 'Organisation' },
    { value: 'organisationMetrics', label: 'Organisation Metrics' },
    { value: 'facilityMetrics', label: 'Facility Metrics' },
    { value: 'reports', label: 'Reports' },
    { value: 'usageData', label: 'Usage Data' },
    { value: 'dashboard', label: 'Dashboard' },
    { value: 'developer', label: 'Developer' },
    {
      label: 'Facilities:', // Group header
      options: [{ value: 'facilities.*', label: 'All Facilities' }, ...facilityOptions],
    },
  ];

  const getValues = (value) => {
    if (['organisationMetrics', 'facilityMetrics', 'usageData', 'dashboard']?.includes(value)) {
      return [
        { value: 'view', label: 'View' },
        { value: 'create', label: 'Create' },
        { value: 'edit', label: 'Edit' },
        { value: 'delete', label: 'Delete' },
      ];
    } else if (value == 'organisation') {
      return [
        { value: 'view', label: 'View' },
        { value: 'edit', label: 'Edit' },
        { value: 'manage', label: 'Manage' },
      ];
    } else if (value == 'reports') {
      return [
        { value: 'view', label: 'View' },
        { value: 'create', label: 'Create' },
      ];
    } else if (value == 'developer') {
      return [
        { value: 'read', label: 'Read' },
        { value: 'write', label: 'Write' },
      ];
    } else {
      return [
        { value: 'view', label: 'View' },
        { value: 'edit', label: 'Edit' },
        { value: 'delete', label: 'Delete' },
      ];
    }
  };

  if (!sortedUserList?.length > 0 && !sortedUserList) return <LinearProgress color='inherit' />;

  return (
    <>
      <div className='col-12'>
        <section className='panel'>
          <header className='panel-heading'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={{ margin: 0 }}>Your Users</p>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'end' }}>
                <div style={{ width: '200px' }}>
                  <Select
                    isClearable={true}
                    options={permissionOptions}
                    styles={customStyles}
                    placeholder='Permission Group'
                    onChange={(e) => {
                      if (!e?.value) {
                        setFilter({ ...filter, permission: null, value: null });
                      } else {
                        setFilter({ ...filter, permission: e?.value });
                      }
                    }}
                    value={permissionOptions?.find((option) => option.value === filter?.permission)}
                  />
                </div>
                <p style={{ margin: 0 }}>:</p>
                <div style={{ width: '200px' }}>
                  <Select
                    isClearable={true}
                    isDisabled={!filter?.permission}
                    options={getValues(filter?.permission)}
                    placeholder='Value'
                    onChange={(e) => {
                      setFilter({ ...filter, value: e?.value });
                    }}
                    value={getValues(filter?.permission)?.find((option) => option.value === filter?.value) || null}
                  />
                </div>
                {branding && branding?.inviteUsers && canManage && (
                  <div>
                    <AddButton id='invite-user-button' onClick={() => setShowInviteUserForm(!showInviteUserForm)}>
                      <i className='fa fa-user-plus me-2' style={{ paddingRight: '0.5rem' }} />
                      Invite User
                    </AddButton>
                  </div>
                )}
              </div>
            </div>
          </header>
          <div className='panel-body'>
            <Card sx={{ borderRadius: 0, boxShadow: 0 }} style={{ fontFamily: body }}>
              <div className='table-responsive'>
                <table className='table table-hover' style={{ marginBottom: '0' }}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      {adminUser && <th>Status</th>}
                      {canManage && <th className='text-center'>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.isArray(sortedUserList) &&
                      sortedUserList
                        .filter((userDetails) => {
                          // Check if the filter is applied
                          if (!filter?.permission || !filter?.value) {
                            return true; // If no filter is applied, include all users
                          }

                          let userPermission;
                          // Check if the user's permissions match the filter
                          if (filter?.permission === 'facilities.*') {
                            userPermission = userDetails?.permissions?.facilities?.['*'];
                          } else if (
                            [
                              'organisation',
                              'organisationMetrics',
                              'facilityMetrics',
                              'reports',
                              'usageData',
                              'dashboard',
                              'developer',
                            ].includes(filter?.permission)
                          ) {
                            userPermission = userDetails?.permissions?.[filter.permission];
                          } else {
                            // Specifc case for facilities
                            userPermission = userDetails?.permissions?.facilities?.[filter?.permission];
                            if (!userPermission && userDetails?.permissions?.facilities?.['*'][filter?.value]) {
                              return true; // If the user doesn't have the permission for the specific facility then assume they have access to it
                            }
                          }
                          return userPermission?.[filter.value] === true;
                        })
                        ?.map((userDetails, index) => (
                          <UsersListRow key={index} userDetails={userDetails} adminUser={adminUser} />
                        ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </section>
      </div>
      <Modal />
      {showInviteUserForm && (
        <InviteUsersForm setShowInviteUserForm={setShowInviteUserForm} showInviteUserForm={showInviteUserForm} />
      )}
    </>
  );
};

const mapStateToProps = (state) => {
  const { cyfAdmin, whiteLabelAdmin, consultantAdmin } = state?.profile?.details || {};
  const adminUser = cyfAdmin || whiteLabelAdmin || consultantAdmin;

  const sortedUserList =
    state.organisation && state.organisation.users && _.sortBy(_.uniqBy(state.organisation.users, 'sub'), 'name');
  return {
    currentOrganisation: state.currentOrganisation,
    branding: state.branding,
    sortedUserList,
    adminUser,
    showLoader: state?.showLoader,
    canManage: !!state?.organisation?.permissions?.organisation?.manage,
    facilityList: state.facility?.list,
  };
};

const mapDispatchToProps = (dispatch) => ({});

export default connect(mapStateToProps, mapDispatchToProps)(UsersListTable);
